/**
 * D-020 V2B — the explicit hotkey-focus model (LT2-013).
 *
 * Exactly ONE derivative card can be hotkey-focused at a time. A card
 * registers a RESOLVER, not a snapshot: the resolver ref is updated every
 * render with exactly the values the card just painted, which is the
 * mechanism that makes "A/D submit the same displayed top-of-book action a
 * mouse click would" true by construction — the listener can never read a
 * price the trader isn't looking at.
 *
 * Card ids are market-identity-stable (`st:<eventKey>:spread`,
 * `gl:<gameKey>:<period>:total`), never positional — a re-sorted board
 * cannot silently retarget the focus (LT2-006). Unregistering (unmount,
 * layout flip, tab/period change re-keys the id) clears the focus if it was
 * held, so a hotkey can never fire at a card that is no longer on screen.
 */
import { create } from 'zustand';
import type { GameRow, VenueBook } from '@/views/ValueDashboard/types';
import type { LineAvailability } from '@/lib/sport/lineAvailability';
import type { Lt2Period, Lt2Surface } from '@/lib/trading/liveIntent';
import type { Lt2AttackTarget } from '@/lib/trading/attackTarget';

export interface Lt2HotkeyTarget {
  book: VenueBook | null;
  side: 'yes' | 'no';
  /** EXACTLY the cents the card is displaying for this action; null = no
   *  displayed price → the hotkey refuses truthfully. */
  priceCents: number | null;
  /** The displayed action's maker semantics (ladder bid = join = true;
   *  pill = the section's price mode). */
  postOnly: boolean;
  /** #724 — the displayed price IS this side's own best bid (a join), so a
   *  post-only order at it rests rather than crosses. Two-sided S posts only
   *  joins. Deliberately separate from `postOnly`: the moneyline pills submit
   *  postOnly:false in every price mode (A/D keep mouse parity), so
   *  `postOnly` cannot say whether a moneyline price is a bid. */
  joinsBid: boolean;
  label: string;
  availability: LineAvailability;
  /** Live Trading V3 — the bounded aggressive action for this same side:
   *  reference (best same-side displayed bid) and target (reference + the
   *  accepted depth). Resolved from exactly the book the card is painting,
   *  like every other field here, so W/E can never act on a price the trader
   *  isn't looking at. Null = no attackable reference right now (no
   *  displayed bid, price hidden, or target out of range) → W/E refuse
   *  truthfully. */
  attack: Lt2AttackTarget | null;
}

export interface Lt2CardTargets {
  /** A = top (vertical pill pair) / LEFT (side-by-side ladder columns). */
  a: Lt2HotkeyTarget | null;
  /** D = bottom / RIGHT. */
  d: Lt2HotkeyTarget | null;
  surface: Lt2Surface;
  marketKind: 'moneyline' | 'spread' | 'total';
  period: Lt2Period;
  line: number | null;
  /** Compact human name for the header target readout ("NYJ ML",
   *  "DAL −3.5", "O/U 47.5"). Presentation metadata only — identity stays
   *  the card id, prices stay the a/d targets. */
  cardLabel: string;
  /** V3 Pull All scope: the focused card's own GAME, as the authoritative
   *  input to the exact-game cancel selection (canonical-key resolution over
   *  live order state — never DOM). Resolver-provided so it is always the
   *  game the trader is looking at. Null = this card cannot anchor a
   *  game-scoped pull, and Spacebar refuses truthfully. */
  pullScope: { game: GameRow } | null;
  /** Presentation-only pause: 'expanded' = the #647 moneyline suppression
   *  (the card keeps its focus, A/D refuse until collapse). Never consulted
   *  by the firing path — the DOM price marks remain the fence's authority. */
  pausedReason: 'expanded' | null;
  /** #724 — the card's game has a third outcome (a soccer Draw). A and D
   *  still target the two teams and S posts both, but NO on both teams of a 1X2
   *  pays on a draw either way, so the pair is a draw position rather than a
   *  two-sided market. Absent = two outcomes. */
  threeWay?: boolean;
}

type Resolver = () => Lt2CardTargets | null;

interface Lt2FocusStore {
  focusedCardId: string | null;
  /** Monotonic presentation-invalidation signal. Bumped by the FOCUSED
   *  card's own re-render (useLt2Card), so the header target readout can
   *  re-derive label/paused state from `resolveFocused()` without polling
   *  and without a second target source. Never read by the firing path. */
  presentationSeq: number;
  notePresentation: () => void;
  setFocus: (id: string) => void;
  /** #648 — the mouse contract. The card's own focus region is the ONE
   *  gesture that changes focus: same card releases, another card takes it.
   *  One store update, so a consumer can never compose a stale
   *  get/clear/set and land two cards claiming focus (which the hotkey
   *  fence treats as ambiguous and refuses). */
  toggleFocus: (id: string) => void;
  /** #648 — a line chip picks a market INSIDE its own card, so it may take a
   *  FREE focus, but it is not the card's focus region: it must never steal
   *  the focus from another card and must never clear. Without this a nested
   *  control could still retarget the hotkeys incidentally, which is the
   *  capture-phase bug #648 exists to end. */
  focusIfFree: (id: string) => void;
  clearFocus: () => void;
  /** #608 — drop the focus only if THIS card holds it. For the case the
   *  unregister teardown cannot cover: the card stays mounted under the same
   *  id while the market it was displaying disappears, so nothing
   *  unregisters, yet the held focus no longer names a market the trader
   *  chose. Equality-guarded so one card can never clear another's focus.
   *  D19 — callers clearing because the market disappeared pass
   *  `{ marketGone: true }`; the aim chip's own ✕ does not. */
  clearFocusIf: (id: string, opts?: { marketGone?: boolean; lineChanged?: boolean; label?: string }) => void;
  /** Register a card's resolver. Returns the unregister fn; unregistering
   *  clears the focus if this card held it. */
  register: (id: string, resolver: Resolver) => () => void;
  /** Resolve the focused card's CURRENT targets; null when nothing is
   *  focused or the focused card's resolver went away. */
  resolveFocused: () => Lt2CardTargets | null;
  /** D19 — the label of an aimed card that left the board (its card
   *  unregistered, or `clearFocusIf` dropped it), so the next bound press can
   *  say so instead of being silent. Any deliberate aim change (a new aim,
   *  `clearFocus`) forgets it. */
  lostAim: string | null;
  lostAimReason: 'market-gone' | 'line-changed' | null;
  /** D19 — read and forget `lostAim`: one refusal per lost aim. */
  takeLostAim: () => string | null;
}

const _resolvers = new Map<string, Resolver>();

/** D19 — the label a card would show for itself, read from its own resolver
 *  as it leaves. A card that cannot answer gets a generic name. */
function labelOf(resolver: Resolver | undefined): string {
  try {
    const label = resolver?.()?.cardLabel;
    if (typeof label === 'string' && label !== '') return label;
  } catch {
    /* a card mid-unmount may not answer */
  }
  return 'The aimed card';
}

export const useLt2FocusStore = create<Lt2FocusStore>((set, get) => ({
  focusedCardId: null,
  presentationSeq: 0,
  lostAim: null,
  lostAimReason: null,

  takeLostAim: () => {
    const label = get().lostAim;
    if (label != null) set({ lostAim: null, lostAimReason: null });
    return label;
  },

  notePresentation: () => set((s) => ({ presentationSeq: s.presentationSeq + 1 })),

  setFocus: (id) => {
    if (!_resolvers.has(id)) return; // only registered cards are focusable
    set({ focusedCardId: id, lostAim: null, lostAimReason: null });
  },

  toggleFocus: (id) => {
    if (!_resolvers.has(id)) return; // only registered cards are focusable
    set((s) => ({ focusedCardId: s.focusedCardId === id ? null : id, lostAim: null, lostAimReason: null }));
  },

  focusIfFree: (id) => {
    if (!_resolvers.has(id)) return;
    const current = get().focusedCardId;
    if (current === null || current === id) set({ focusedCardId: id, lostAim: null, lostAimReason: null });
  },

  clearFocus: () => set({ focusedCardId: null, lostAim: null, lostAimReason: null }),

  clearFocusIf: (id, opts) => {
    if (get().focusedCardId !== id) return;
    // D19 — `marketGone`: the market under the aim disappeared, which is not
    // the trader's choice, so the next press says so. Without it this is a
    // deliberate clear (the aim chip's ✕) and nothing is remembered.
    set({
      focusedCardId: null,
      lostAim: opts?.marketGone || opts?.lineChanged ? (opts.label ?? labelOf(_resolvers.get(id))) : null,
      lostAimReason: opts?.marketGone ? 'market-gone' : opts?.lineChanged ? 'line-changed' : null,
    });
  },

  register: (id, resolver) => {
    _resolvers.set(id, resolver);
    return () => {
      // Another register may have replaced this id (StrictMode double
      // mount); only delete if we still own it.
      if (_resolvers.get(id) === resolver) {
        // D19 — the aimed card left the board; remember what it was.
        const lostAim = get().focusedCardId === id ? labelOf(resolver) : null;
        _resolvers.delete(id);
        if (lostAim != null) set({ focusedCardId: null, lostAim, lostAimReason: 'market-gone' });
      }
    };
  },

  resolveFocused: () => {
    const id = get().focusedCardId;
    if (id == null) return null;
    const resolver = _resolvers.get(id);
    return resolver ? resolver() : null;
  },
}));
